import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { ApiError, api, downloads, type Decision, type Screening } from "../api/client";
import "../components/ld-score-ring";
import "../components/ld-signal-strip";
import "../components/ld-voyant-section";
import { formatBirth, formatDateTime, initials } from "../format";
import { icons } from "../icons";
import { navigate } from "../router";
import { DECISIONS, badgeClass, stateView } from "../signal-state";
import { shared } from "../styles/shared";

@customElement("page-report")
export class PageReport extends LitElement {
  @property() screeningId = "";

  @state() private screening: Screening | null = null;
  @state() private loadError = "";
  @state() private rerunning = false;
  @state() private decision: Decision | null = null;
  @state() private comment = "";
  @state() private saving = false;
  @state() private reviewError = "";
  @state() private conflict = false;
  @state() private saved = false;

  static styles = [
    shared,
    css`
      :host { display: block; padding-bottom: 80px; }
      .top { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
      .summary { display: grid; grid-template-columns: 1.3fr 1fr; gap: 20px; margin: 32px 0; }
      @media (max-width: 900px) { .summary { grid-template-columns: 1fr; } }
      .identity { display: flex; align-items: center; gap: 16px; margin-bottom: 22px; }
      .identity-avatar {
        width: 56px; height: 56px; border-radius: 16px; flex-shrink: 0; color: var(--white);
        background: linear-gradient(155deg, var(--navy-700), var(--navy-950));
        display: flex; align-items: center; justify-content: center; font-size: 19px; font-weight: 700;
      }
      .identity-name { font-size: 19px; font-weight: 800; letter-spacing: -.01em; }
      .identity-meta { font-size: 13px; color: var(--slate-600); margin-top: 2px; }
      .info-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--slate-200); }
      .info-row:last-child { border-bottom: none; }
      .info-row .k { font-size: 13px; color: var(--slate-600); }
      .info-row .v { font-size: 13.5px; font-weight: 600; font-family: var(--font-mono); text-align: right; }
      .strip { margin-top: 22px; }
      .strip .label { display: block; margin-bottom: 10px; }
      .links { display: flex; flex-direction: column; gap: 8px; margin-top: 18px; }
      .source-link {
        display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: 1px solid var(--slate-200);
        border-radius: var(--r-sm); background: var(--white); width: 100%; text-align: left; color: inherit;
        transition: all .15s var(--ease);
      }
      .source-link:hover { border-color: var(--blue-500); background: var(--blue-50); }
      .source-link .name { font-size: 13.5px; font-weight: 600; flex: 1; }
      .source-link svg { width: 14px; height: 14px; color: var(--slate-400); }
      .score { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; gap: 16px; }
      .score .label { font-size: 13px; }
      .score-actions { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }
      .parent { font-size: 12.5px; color: var(--slate-400); }
      .parent a { color: var(--blue-600); }

      .global { border-radius: var(--r-lg); padding: 26px 28px; margin: 8px 0 32px; display: flex; gap: 18px; align-items: flex-start; border: 1px solid; }
      .global[data-tone="green"] { background: var(--green-bg); border-color: var(--green-line); }
      .global[data-tone="amber"] { background: var(--amber-bg); border-color: var(--amber-line); }
      .global[data-tone="red"] { background: var(--red-bg); border-color: var(--red-line); }
      .global[data-tone="neutral"] { background: var(--neutral-bg); border-color: var(--neutral-line); }
      .global-icon { width: 40px; height: 40px; border-radius: 11px; background: var(--white); display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: var(--shadow-xs); }
      .global-icon svg { width: 20px; height: 20px; }
      .global[data-tone="green"] .global-icon { color: var(--green); }
      .global[data-tone="amber"] .global-icon { color: var(--amber); }
      .global[data-tone="red"] .global-icon { color: var(--red); }
      .global[data-tone="neutral"] .global-icon { color: var(--slate-600); }
      .global h2 { font-size: 15.5px; font-weight: 700; margin-bottom: 6px; }
      .global p { font-size: 13.5px; color: var(--slate-600); line-height: 1.6; max-width: 680px; }

      .review { margin-bottom: 40px; }
      .review h2 { font-size: 16px; font-weight: 700; margin-bottom: 4px; }
      .review > p { font-size: 13.5px; color: var(--slate-600); margin-bottom: 18px; }
      .decisions { display: flex; gap: 8px; flex-wrap: wrap; margin: 8px 0 16px; }
      .decision { border: 1px solid var(--slate-200); background: var(--white); border-radius: var(--r-pill); padding: 8px 16px; font-size: 13px; font-weight: 600; color: var(--slate-600); }
      .decision[aria-pressed="true"][data-tone="green"] { background: var(--green-bg); color: var(--green); border-color: var(--green-line); }
      .decision[aria-pressed="true"][data-tone="amber"] { background: var(--amber-bg); color: var(--amber); border-color: var(--amber-line); }
      .decision[aria-pressed="true"][data-tone="red"] { background: var(--red-bg); color: var(--red); border-color: var(--red-line); }
      .review-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-top: 14px; }
      .review-foot .muted { font-size: 12.5px; }
      .review .alert { margin-top: 14px; }

      .eyebrow-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
      .manual { display: flex; gap: 8px; flex-wrap: wrap; }
      .state-box { padding: 120px 0; display: flex; justify-content: center; color: var(--slate-400); }
    `,
  ];

  protected willUpdate(changed: PropertyValues<this>): void {
    if (changed.has("screeningId") && this.screeningId) void this.load();
  }

  private async load() {
    this.screening = null;
    this.loadError = "";
    try {
      this.applyScreening(await api.screening(this.screeningId));
    } catch (err) {
      this.loadError = err instanceof ApiError ? err.message : "Rapport indisponible.";
    }
  }

  private applyScreening(screening: Screening) {
    this.screening = screening;
    this.decision = screening.review.decision;
    this.comment = screening.review.comment;
    this.conflict = false;
    this.reviewError = "";
  }

  private async rerun() {
    if (!this.screening) return;
    this.rerunning = true;
    try {
      const fresh = await api.rerun(this.screening.id);
      navigate(`/rapports/${fresh.id}`);
    } catch (err) {
      this.reviewError = err instanceof ApiError ? err.message : "Relance impossible.";
    } finally {
      this.rerunning = false;
    }
  }

  private async saveReview(event: SubmitEvent) {
    event.preventDefault();
    if (!this.screening) return;
    this.saving = true;
    this.reviewError = "";
    this.saved = false;
    try {
      this.applyScreening(
        await api.review(this.screening.id, {
          decision: this.decision,
          comment: this.comment,
          version: this.screening.version,
        }),
      );
      this.saved = true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) this.conflict = true;
      this.reviewError = err instanceof ApiError ? err.message : "Enregistrement impossible.";
    } finally {
      this.saving = false;
    }
  }

  private scrollToSource(code: string) {
    const section = this.renderRoot.querySelector<HTMLElement & { open: boolean }>(`#voyant-${code}`);
    if (!section) return;
    section.open = true;
    section.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  private renderGlobal(s: Screening) {
    const flagged = s.results.filter((r) => r.state === "possible_match" || r.state === "strong_match");
    const missing = s.results.filter((r) => r.state === "unavailable" || r.state === "not_checked");
    const tone = stateView(s.overall_state).tone;
    let title: string;
    let text: string;
    if (s.overall_state === "clear") {
      title = "Aucune correspondance détectée";
      text = `Toutes les sources ont été consultées pour ${s.person.full_name} et aucune ne contient l'identité recherchée.`;
    } else if (flagged.length) {
      title = `${flagged.length} source${flagged.length > 1 ? "s" : ""} à examiner`;
      text = `Revue manuelle nécessaire avant de conclure : ${flagged.map((r) => r.label).join(", ")}.`;
      if (missing.length) text += ` Vérification incomplète : ${missing.map((r) => r.label).join(", ")}.`;
    } else {
      title = "Vérification incomplète";
      text = `Aucune correspondance dans les sources consultées, mais ${missing.map((r) => r.label).join(", ")} n'${missing.length > 1 ? "ont" : "a"} pas pu être vérifiée${missing.length > 1 ? "s" : ""}. Ce n'est pas une absence de résultat.`;
    }
    return html`<section class="global" data-tone=${tone} aria-live="polite">
      <span class="global-icon">${tone === "green" ? icons.check : icons.alert}</span>
      <div><h2>${title}</h2><p>${text}</p></div>
    </section>`;
  }

  private renderReview(s: Screening) {
    const r = s.review;
    return html`<form class="card card-padded review" @submit=${this.saveReview}>
      <h2>Conclusion de l'enquêteur</h2>
      <p>Les voyants signalent ; la conclusion vous appartient. Un commentaire est exigé pour conclure « Conforme » si un voyant n'est pas vert.</p>
      <span class="label">Décision</span>
      <div class="decisions" role="group" aria-label="Décision">
        ${(Object.keys(DECISIONS) as Decision[]).map(
          (key) => html`<button
            type="button"
            class="decision"
            data-tone=${DECISIONS[key].tone}
            aria-pressed=${this.decision === key}
            @click=${() => (this.decision = this.decision === key ? null : key)}
          >${DECISIONS[key].label}</button>`,
        )}
      </div>
      <div class="field">
        <label for="comment">Commentaire interne</label>
        <textarea class="input" id="comment" maxlength="2000" placeholder="Ex. homonyme : date de naissance différente sur la fiche DGT"
          .value=${this.comment} @input=${(e: Event) => (this.comment = (e.target as HTMLTextAreaElement).value)}></textarea>
      </div>
      ${this.reviewError
        ? html`<div class="alert alert-error" role="alert">${icons.alert}<span>${this.reviewError}
            ${this.conflict ? html` <button type="button" class="btn btn-secondary btn-sm" @click=${this.load}>Recharger</button>` : nothing}</span></div>`
        : nothing}
      <div class="review-foot">
        <span class="muted">${r.reviewed_at ? `Dernière revue par ${r.reviewed_by_name} le ${formatDateTime(r.reviewed_at)}` : "Pas encore revu"}${this.saved ? " · enregistré" : ""}</span>
        <button class="btn btn-primary btn-sm" ?disabled=${this.saving}>${this.saving ? html`<span class="spinner"></span>` : nothing} Enregistrer</button>
      </div>
    </form>`;
  }

  render() {
    if (this.loadError)
      return html`<div class="container page-header">
        <div class="alert alert-error" role="alert">${icons.alert}<span>${this.loadError}</span></div>
      </div>`;
    const s = this.screening;
    if (!s) return html`<div class="state-box"><span class="spinner"></span></div>`;

    const decision = s.review.decision ? DECISIONS[s.review.decision] : null;
    return html`
      <div class="page-header">
        <div class="container top">
          <div>
            <div class="page-eyebrow">Rapport de criblage</div>
            <h1 class="page-title">Résultats de la recherche</h1>
            <p class="page-sub">Synthèse des vérifications, avec accès direct aux fiches des sources consultées.</p>
          </div>
          ${decision ? html`<span class=${badgeClass(decision.tone)}>Conclusion : ${decision.label}</span>` : nothing}
        </div>
      </div>

      <div class="container">
        <div class="summary">
          <div class="card card-padded">
            <div class="identity">
              <span class="identity-avatar">${initials(s.person.full_name)}</span>
              <div>
                <div class="identity-name">${s.person.full_name}</div>
                <div class="identity-meta">Recherche du ${formatDateTime(s.created_at)} par ${s.created_by_name}</div>
              </div>
            </div>
            <div class="info-row"><span class="k">Nom</span><span class="v">${s.person.last_name}</span></div>
            <div class="info-row"><span class="k">Prénom</span><span class="v">${s.person.first_name ?? "—"}</span></div>
            <div class="info-row"><span class="k">Naissance</span><span class="v">${formatBirth(s.person.birth_date)}</span></div>
            <div class="info-row"><span class="k">Sources interrogées</span><span class="v">${s.source_count}</span></div>
            <div class="strip">
              <span class="label">État par source</span>
              <ld-signal-strip .items=${s.results.map((r) => ({ source: r.source, state: r.state }))}></ld-signal-strip>
            </div>
            <div class="links">
              ${s.results.map(
                (r) => html`<button class="source-link" @click=${() => this.scrollToSource(r.source)}>
                  <ld-signal compact .code=${r.source} .state=${r.state}></ld-signal>
                  <span class="name">${r.label}</span>
                  <span class=${badgeClass(stateView(r.state).tone)}>${stateView(r.state).label}</span>
                </button>`,
              )}
            </div>
          </div>

          <div class="card card-padded score">
            <ld-score-ring .value=${s.clear_count} .total=${s.source_count}></ld-score-ring>
            <span class="label">Sources sans correspondance</span>
            <div class="score-actions">
              <a class="btn btn-secondary btn-sm" href=${downloads.screeningCsv(s.id)} download>${icons.download} CSV</a>
              <a class="btn btn-secondary btn-sm" href=${downloads.screeningPdf(s.id)} download title="Rapport PDF généré par le serveur à partir du criblage enregistré">${icons.file} PDF</a>
              <button class="btn btn-secondary btn-sm" @click=${this.rerun} ?disabled=${this.rerunning}>
                ${this.rerunning ? html`<span class="spinner"></span>` : icons.refresh} Relancer
              </button>
            </div>
            ${s.parent_id ? html`<span class="parent">Relance de <a href="/rapports/${s.parent_id}">la recherche précédente</a></span>` : nothing}
          </div>
        </div>

        ${this.renderGlobal(s)}
        ${this.renderReview(s)}

        <div class="eyebrow-row">
          <div class="page-eyebrow">Détail par voyant</div>
          <div class="manual">
            ${s.manual_links.map(
              (l) => html`<a class="btn btn-ghost btn-sm" href=${l.url} target="_blank" rel="noopener noreferrer">${icons.external} ${l.label}</a>`,
            )}
          </div>
        </div>
        ${s.results.map(
          (r, i) => html`<ld-voyant-section
            id="voyant-${r.source}"
            .result=${r}
            .index=${i}
            ?open=${r.state !== "clear"}
          ></ld-voyant-section>`,
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-report": PageReport;
  }
}
